import type { CSSProperties } from 'react'

import type { TopbarMode } from '@/types/digitalProduct'

/**
 * `clean` e `immersive` são presets fixos, definidos em código. `themed` é o
 * modo dirigido pela IDV do evento — modo claro/escuro escolhido pelo produtor,
 * com textura ou cor de fundo próprias.
 */
export type TopbarVariant = 'clean' | 'immersive' | 'themed'

/**
 * A skin decide o tratamento de *conteúdo* da topbar — cor de numeral, pill da
 * wallet, divisória e imagem de fallback da logo. É deliberadamente separada do
 * background: `immersive` e `themed`+dark compartilham a mesma skin escura, mas
 * têm fundos completamente diferentes.
 */
export type TopbarSkin = 'light' | 'dark'

/**
 * Fundo aplicado quando o produtor escolheu um modo mas não configurou cor nem
 * textura. `dark` é o valor do token `--color-dark` (styles/tailwind.css).
 *
 * Espelha `TOPBAR_PREVIEW_DEFAULT_BACKGROUND` do TopbarPreview (webapp) — os
 * dois apps são repositórios separados, então manter em sincronia é manual.
 */
export const TOPBAR_DEFAULT_BACKGROUND: Record<TopbarSkin, string> = {
  light: '#FFFFFF',
  dark: '#17161B'
}

const TOPBAR_MODES: readonly TopbarMode[] = ['light', 'dark']

/**
 * Um evento que nunca customizou a topbar chega aqui com `topbar_mode` nulo e
 * precisa continuar na variante `clean` — é o que preserva o visual atual sem
 * migração de dados.
 */
export function resolveTopbarVariant(
  topbarMode: TopbarMode | null | undefined
): TopbarVariant {
  if (!topbarMode || !TOPBAR_MODES.includes(topbarMode)) return 'clean'

  return 'themed'
}

export function resolveTopbarSkin(
  variant: TopbarVariant,
  mode?: TopbarMode | null
): TopbarSkin {
  if (variant === 'immersive') return 'dark'
  if (variant === 'themed') return mode === 'dark' ? 'dark' : 'light'

  return 'light'
}

type ResolveTopbarBackgroundArgs = {
  variant: TopbarVariant
  mode?: TopbarMode | null
  textureUrl?: string | null
  colorLight?: string | null
  colorDark?: string | null
}

/**
 * Só `themed` produz background dinâmico: `clean` usa a classe `bg-white` e
 * `immersive` carrega o próprio asset estático, ambos no componente.
 *
 * Precedência: textura (em tile) > cor de fallback do modo ativo > default do
 * modo. A textura usa `background-repeat` e **não** pode ganhar `cover`, que
 * anularia a repetição.
 */
export function resolveTopbarBackground({
  variant,
  mode,
  textureUrl,
  colorLight,
  colorDark
}: ResolveTopbarBackgroundArgs): CSSProperties | undefined {
  if (variant !== 'themed') return undefined

  if (textureUrl) {
    return {
      backgroundImage: `url(${textureUrl})`,
      backgroundRepeat: 'repeat'
    }
  }

  const skin = resolveTopbarSkin(variant, mode)
  const configuredColor = skin === 'dark' ? colorDark : colorLight

  return { backgroundColor: configuredColor || TOPBAR_DEFAULT_BACKGROUND[skin] }
}
